import {
	ComponentMirror,
	Injectable,
	InjectionToken,
	Injector,
	reflectComponentType,
	Type,
	ViewContainerRef,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { ignoreNil } from "@obg/ngx.global";
import { RouteConfig, RouteSelector } from "@obg/ngx.router";
import { Observable, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { DynamicLayoutHandlerAttachOptions } from "./dynamic-layout.model";

@Injectable({
	providedIn: "root",
})
export class DynamicLayoutHandler {
	private componentInjectorMap = new Map<Type<unknown>, Injector>();
	private data$$: Subscription | undefined;
	private currentLayout: ComponentMirror<unknown> | undefined;

	constructor(
		private injector: Injector,
		private store: Store<any>
	) {}

	attach(viewContainerRef: ViewContainerRef, options?: DynamicLayoutHandlerAttachOptions) {
		if (this.data$$) {
			return;
		}

		this.data$$ = this.getRouteLayoutComponent().subscribe(
			(layout) => this.setLayout(layout, viewContainerRef),
			options?.errorHandler
		);
	}

	detach() {
		this.data$$?.unsubscribe();
	}

	dispose() {
		this.data$$ = undefined;
		this.currentLayout = undefined;
	}

	getLayoutFromRoute(route: RouteConfig | undefined): Type<any> | undefined {
		const layoutOrToken = route?.layout;
		return layoutOrToken instanceof InjectionToken ? this.injector.get(layoutOrToken) : layoutOrToken;
	}

	registerComponentInjector(component: Type<unknown>, injector: Injector) {
		this.componentInjectorMap.set(component, injector);
	}

	setLayout(component: Type<unknown>, viewContainerRef: ViewContainerRef) {
		if (this.currentLayout?.selector === reflectComponentType(component)?.selector) {
			return;
		}

		viewContainerRef.clear();
		const componentRef = viewContainerRef.createComponent(component, {
			injector: this.componentInjectorMap.get(component),
		});
		componentRef.changeDetectorRef.markForCheck();
		componentRef.onDestroy(() => componentRef.hostView.detectChanges());
		this.currentLayout = reflectComponentType(component) ?? undefined;
	}

	private getRouteLayoutComponent(): Observable<Type<any>> {
		return this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			distinctUntilChanged((x, y) => x.layout === y.layout),
			map((route) => this.getLayoutFromRoute(route)),
			ignoreNil()
		);
	}
}
